/**
 * `api.ts` runs outside the component tree (TanStack Query calls it), but a 401 has to bounce the
 * user to `/login`. Rather than threading a callback through every query, it's broadcast here and
 * picked up by `RequireAuth` on mount — the same tiny pub/sub channel this module always used.
 *
 * `pushToast` (REV16 — docs/specs/REV16-clearboss-and-toastify.md) used to be the other half of
 * that pub/sub, feeding the in-house `<Toaster />`. It's now a thin wrapper over react-toastify's
 * own `toast()` — same import path and call shape for the ~10 existing call sites (`api.ts`'s 429
 * handler, every optimistic mutation's `onError` rollback, plus REV16's `clearboss` success toast),
 * with an added optional `variant`. The old pub-sub (`Toast`, `subscribeToToasts`) and
 * `components/Toaster.tsx` are gone — `<ToastContainer />` in `App.tsx` renders everything now.
 *
 * REV18: imports from `react-toastify/unstyled`, not the package root — see App.tsx's import for
 * why (the *styled* entry's `<ToastContainer>` self-injects react-toastify's own default CSS at
 * mount time, which silently beat our themed overrides in index.css). `toast()` itself doesn't
 * self-inject anything either way, but both entry points have to agree on the same toast registry,
 * so this has to import from wherever `<ToastContainer>` does.
 */
import { toast } from 'react-toastify/unstyled';

type ToastVariant = 'info' | 'success' | 'error' | 'warning';

type VoidListener = () => void;

const unauthorizedListeners = new Set<VoidListener>();

/**
 * Every pre-REV16 call site is a failure message (a 429 warning or a rollback-after-error), so
 * `'error'` is the default variant — the closest match to the old toaster's one-size-fits-all look
 * for those cases. Callers with a happier message (REV16's `clearboss` success toast) pass
 * `'success'` explicitly.
 */
export function pushToast(message: string, variant: ToastVariant = 'error'): void {
  toast(message, { type: variant });
}

export function subscribeToUnauthorized(listener: VoidListener): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

export function reportUnauthorized(): void {
  for (const listener of unauthorizedListeners) {
    listener();
  }
}
